import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import PreferencesHero from './components/PreferencesHero';
import EmailIdentifier from './components/EmailIdentifier';
import FrequencyToggle from './components/FrequencyToggle';
import CategoryCheckboxes from './components/CategoryCheckboxes';
import DangerZone from './components/DangerZone';
import SavePreferences from './components/SavePreferences';
import Icon from '../../components/AppIcon';

const NewsletterPreferencesManagementPage = () => {
  const [email, setEmail] = useState('');
  const [frequency, setFrequency] = useState('weekly');
  const [categories, setCategories] = useState([]);
  const [loadingPrefs, setLoadingPrefs] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [recordId, setRecordId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [unsubscribeLoading, setUnsubscribeLoading] = useState(false);
  const [unsubscribed, setUnsubscribed] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [saveShake, setSaveShake] = useState(false);

  const handleLoadPreferences = async () => {
    if (!email?.trim()) {
      setLoadError('Vennligst skriv inn e-postadressen din.');
      return;
    }
    setLoadingPrefs(true);
    setLoadError('');
    setLoaded(false);
    setSaveSuccess(false);

    try {
      const { data, error } = await supabase
        ?.from('contact_optins')
        ?.select('*')
        ?.eq('email', email?.trim())
        ?.order('created_at', { ascending: false })
        ?.limit(1)
        ?.single();

      if (error && error?.code !== 'PGRST116') {
        setLoadError('Feil ved lasting av innstillinger. Prøv igjen.');
      } else if (!data) {
        setLoadError('Ingen abonnement funnet for denne e-postadressen.');
      } else {
        setRecordId(data?.id);
        setFrequency(data?.frequency || 'weekly');
        setCategories(data?.categories || []);
        setUnsubscribed(data?.unsubscribed || false);
        setLoaded(true);
      }
    } catch (err) {
      setLoadError('Noe gikk galt. Prøv igjen senere.');
    } finally {
      setLoadingPrefs(false);
    }
  };

  const handleSavePreferences = async () => {
    if (!email?.trim()) {
      setLoadError('Last inn innstillingene dine først.');
      setSaveShake(true);
      setTimeout(() => setSaveShake(false), 600);
      return;
    }
    setSaving(true);
    setSaveSuccess(false);

    try {
      if (recordId) {
        const { error } = await supabase
          ?.from('contact_optins')
          ?.update({
            frequency,
            categories,
            updated_at: new Date()?.toISOString()
          })
          ?.eq('id', recordId);

        if (error) throw error;
      } else {
        const { data, error } = await supabase
          ?.from('contact_optins')
          ?.insert({
            email: email?.trim(),
            optin_email: true,
            frequency,
            categories,
            source: 'preferences_page'
          })
          ?.select()
          ?.single();

        if (error) throw error;
        setRecordId(data?.id);
      }
      setSaveSuccess(true);
      setShowToast(true);
      setTimeout(() => setShowToast(false), 4500);
    } catch (err) {
      setLoadError('Feil ved lagring. Prøv igjen.');
      setSaveShake(true);
      setTimeout(() => setSaveShake(false), 600);
    } finally {
      setSaving(false);
    }
  };

  const handleUnsubscribe = async () => {
    if (!email?.trim()) return;
    setUnsubscribeLoading(true);

    try {
      if (recordId) {
        await supabase
          ?.from('contact_optins')
          ?.update({
            unsubscribed: true,
            optin_email: false,
            unsubscribed_at: new Date()?.toISOString()
          })
          ?.eq('id', recordId);
      }
      setUnsubscribed(true);
    } catch (err) {
      // silently handle
    } finally {
      setUnsubscribeLoading(false);
    }
  };

  if (unsubscribed) {
    return (
      <div style={{ background: '#050A12', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 5%' }}>
        <div style={{ textAlign: 'center', maxWidth: '480px' }}>
          {/* Polaroid-style unsubscribe card */}
          <div style={{
            display: 'inline-block',
            background: '#EEF0F2',
            padding: '24px 24px 40px',
            transform: 'rotate(1.5deg)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
            marginBottom: '32px'
          }}>
            <div style={{
              width: '200px', height: '120px',
              background: 'linear-gradient(135deg, #B41414 0%, rgba(180,20,20,0.5) 100%)',
              marginBottom: '16px',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <Icon name="UserX" size={40} color="#fff" />
            </div>
            <div style={{
              fontFamily: 'Georgia, serif', fontStyle: 'italic',
              fontSize: '0.875rem', color: '#050A12', textAlign: 'center'
            }}>Avmeldt</div>
          </div>

          <div style={{
            fontFamily: 'Impact, Arial Black, sans-serif',
            fontSize: 'clamp(32px, 5vw, 56px)',
            fontWeight: 900, color: '#EEF0F2',
            letterSpacing: '-0.03em', textTransform: 'uppercase',
            marginBottom: '16px'
          }}>AVMELDT</div>

          <p style={{
            fontFamily: 'Georgia, serif', fontStyle: 'italic',
            fontSize: '1rem', color: 'rgba(138,149,163,0.7)',
            lineHeight: 1.65, marginBottom: '32px'
          }}>
            Du er nå avmeldt fra NordPass nyhetsbrev. Vi beklager å se deg gå — du er alltid velkommen tilbake.
          </p>

          <Link
            to="/"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              padding: '14px 32px',
              background: '#3B6FD4', border: 'none',
              color: '#EEF0F2',
              fontFamily: 'Syne, sans-serif',
              fontSize: '0.875rem', fontWeight: 700,
              letterSpacing: '0.06em', textTransform: 'uppercase',
              textDecoration: 'none',
              transition: 'background 200ms ease'
            }}
          >
            <Icon name="Home" size={14} color="#EEF0F2" />
            Tilbake til forsiden
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: '#050A12', minHeight: '100vh' }}>
      <style>{`
        @keyframes toastSlideIn {
          from { transform: translateY(100px); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
        @keyframes toastSlideOut {
          from { transform: translateY(0);    opacity: 1; }
          to   { transform: translateY(100px); opacity: 0; }
        }
        @keyframes checkDraw {
          from { stroke-dashoffset: 40; }
          to   { stroke-dashoffset: 0; }
        }
        @keyframes saveShake {
          0%, 100% { transform: translateX(0); }
          15% { transform: translateX(-6px); }
          30% { transform: translateX(6px); }
          45% { transform: translateX(-5px); }
          60% { transform: translateX(5px); }
          75% { transform: translateX(-3px); }
          90% { transform: translateX(3px); }
        }
        .save-shake { animation: saveShake 0.6s ease; }
      `}</style>

      {/* Success Toast */}
      {showToast && (
        <div style={{
          position: 'fixed',
          bottom: '32px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 9999,
          animation: 'toastSlideIn 0.4s cubic-bezier(0.34,1.56,0.64,1) forwards'
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '14px',
            background: '#0A1220',
            border: '1px solid rgba(34,197,94,0.35)',
            padding: '16px 28px',
            boxShadow: '0 8px 40px rgba(0,0,0,0.6), 0 0 0 1px rgba(34,197,94,0.1)',
            minWidth: '300px'
          }}>
            {/* Animated checkmark circle */}
            <div style={{
              width: '36px', height: '36px', flexShrink: 0,
              background: 'rgba(34,197,94,0.12)',
              border: '1px solid rgba(34,197,94,0.4)',
              borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth="2.5" strokeLinecap="square">
                <polyline
                  points="20 6 9 17 4 12"
                  strokeDasharray="40"
                  strokeDashoffset="0"
                  style={{ animation: 'checkDraw 0.5s ease 0.2s both' }}
                />
              </svg>
            </div>
            <div>
              <div style={{
                fontFamily: 'Syne, sans-serif',
                fontSize: '0.75rem', fontWeight: 700,
                color: '#22C55E', letterSpacing: '0.12em',
                textTransform: 'uppercase', marginBottom: '3px'
              }}>LAGRET</div>
              <div style={{
                fontFamily: 'Georgia, serif', fontStyle: 'italic',
                fontSize: '0.875rem', color: 'rgba(238,240,242,0.75)',
                lineHeight: 1.4
              }}>Dine nyhetsbrevpreferanser er oppdatert.</div>
            </div>
            {/* Orange accent bar */}
            <div style={{
              position: 'absolute', bottom: 0, left: 0,
              height: '3px', width: '100%',
              background: 'linear-gradient(to right, #22C55E, #3B6FD4)'
            }} />
          </div>
        </div>
      )}

      {/* Navigation back link */}
      <div style={{
        position: 'absolute', top: '20px', left: '5%',
        zIndex: 10
      }}>
        <Link
          to="/"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '8px',
            fontFamily: 'Syne, sans-serif',
            fontSize: '0.75rem', fontWeight: 700,
            color: 'rgba(238,240,242,0.6)',
            letterSpacing: '0.1em', textTransform: 'uppercase',
            textDecoration: 'none',
            transition: 'color 150ms ease'
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = '#EEF0F2'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(238,240,242,0.6)'; }}
        >
          <Icon name="ArrowLeft" size={13} color="currentColor" />
          NordPass
        </Link>
      </div>

      {/* Hero */}
      <PreferencesHero />

      {/* Main content */}
      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '60px 5% 80px' }}>
        {/* Intro label */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: '16px',
          marginBottom: '48px'
        }}>
          <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.06)' }} />
          <span style={{
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: '0.65rem', color: 'rgba(138,149,163,0.4)',
            letterSpacing: '0.2em', textTransform: 'uppercase'
          }}>ADMINISTRER ABONNEMENT</span>
          <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.06)' }} />
        </div>

        {/* Asymmetric grid layout */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: '0'
        }}>
          {/* Email identifier */}
          <EmailIdentifier
            email={email}
            setEmail={setEmail}
            onLoad={handleLoadPreferences}
            loading={loadingPrefs}
            error={loadError}
            loaded={loaded}
          />

          {/* Frequency toggle */}
          <FrequencyToggle
            frequency={frequency}
            setFrequency={setFrequency}
          />

          {/* Category checkboxes */}
          <CategoryCheckboxes
            categories={categories}
            setCategories={setCategories}
          />

          {/* Save preferences */}
          <div className={saveShake ? 'save-shake' : ''}>
            <SavePreferences
              onSave={handleSavePreferences}
              saving={saving}
              success={saveSuccess}
              disabled={!email?.trim()}
            />
          </div>

          {/* Danger zone */}
          <DangerZone
            onUnsubscribe={handleUnsubscribe}
            unsubscribeLoading={unsubscribeLoading}
          />
        </div>

        {/* Footer note */}
        <div style={{
          marginTop: '48px',
          padding: '24px',
          background: 'rgba(59,111,212,0.05)',
          border: '1px solid rgba(59,111,212,0.12)',
          display: 'flex', alignItems: 'flex-start', gap: '14px'
        }}>
          <Icon name="Info" size={16} color="rgba(59,111,212,0.6)" style={{ marginTop: '2px', flexShrink: 0 }} />
          <p style={{
            fontFamily: 'Georgia, serif', fontStyle: 'italic',
            fontSize: '0.875rem', color: 'rgba(138,149,163,0.6)',
            lineHeight: 1.65, margin: 0
          }}>
            Dine personvernrettigheter er viktige for oss. Les vår{' '}
            <Link
              to="/privacy-policy-personvern"
              style={{ color: '#3B6FD4', textDecoration: 'none' }}
            >personvernpolicy</Link>{' '}
            for mer informasjon om hvordan vi behandler dine data.
          </p>
        </div>
      </div>
    </div>
  );
};

export default NewsletterPreferencesManagementPage;
